import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import JobBilling from '../components/JobBilling';
import Modal from '../components/Modal';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const TYPES = [
  { key: 'sim_repair', label: 'Sim repair' },
  { key: 'sim_move', label: 'Simulator move' },
  { key: 'sim_upgrade', label: 'Simulator upgrade' },
  { key: 'site_visit', label: 'Site visit' },
  { key: 'other', label: 'Other' },
];
const COMPONENT_CATEGORIES = [
  'CPU', 'GPU / Video card', 'Motherboard', 'RAM', 'Storage — SSD', 'Storage — HDD',
  'Power supply', 'Case', 'CPU cooler', 'Case fan', 'Network card', 'Capture card',
  'I/O board', 'Sound card', 'Monitor / Display', 'Projector', 'Image generator',
  'Visual channel PC', 'Instructor station PC', 'Control loading', 'Motion controller',
  'USB hub / controller', 'Cabling / Harness', 'Operating system / Software license', 'Other',
];
const typeLabel = (t) => (TYPES.find((x) => x.key === t) || {}).label || t;
const emptyForm = () => ({ customer_id: '', type: 'sim_repair', title: '', description: '', status: 'open', location: '', scheduled_date: '', is_contract: false, move_from: {}, move_to: {} });
const emptyAddr = () => ({ line1: '', line2: '', city: '', state: '', postal: '', floor: '' });

const toLocalInput = (ts) => (ts ? new Date(ts).toISOString().slice(0, 16) : '');
const fmtDT = (ts) => (ts ? new Date(ts).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const emptyTravel = () => ({ staff_id: '', depart_at: '', return_at: '', airline: '', flight_confirmation: '', flight_numbers: '', flight_route: '', hotel_name: '', hotel_confirmation: '', hotel_check_in: '', hotel_check_out: '', car_company: '', car_confirmation: '', car_pickup_at: '', car_return_at: '', notes: '' });

// Resize a chosen image down to a sane size and return a JPEG data URI.
function resizeImage(file, maxDim = 1920, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else { width = Math.round((width * maxDim) / height); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      try { resolve(canvas.toDataURL('image/jpeg', quality)); }
      catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image (HEIC photos may need to be JPEG).')); };
    img.src = url;
  });
}

// One photo tile: lazily fetches its image and shows a thumbnail; click to enlarge.
function PhotoTile({ jobId, photo, canEdit, onDelete, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    api(`/jobs/${jobId}/photos/${photo.id}`).then((d) => { if (live) setSrc(d.image_data); }).catch(() => {});
    return () => { live = false; };
  }, [jobId, photo.id]);
  return (
    <div className="photo-tile">
      {src ? <img src={src} alt={photo.caption || ''} onClick={() => onOpen(src)} />
           : <div className="photo-tile__ph">…</div>}
      {canEdit && <button className="photo-tile__rm" title="Delete" onClick={() => onDelete(photo.id)}>×</button>}
    </div>
  );
}

export default function Jobs() {
  const { activeId } = useBusiness();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const editable = can('jobs.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [ingestEnabled, setIngestEnabled] = useState(false);
  const [mailEnabled, setMailEnabled] = useState(false);
  const [checking, setChecking] = useState(false);
  const [diag, setDiag] = useState(null);
  const [notice, setNotice] = useState(null);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [openRowInfo, setOpenRowInfo] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [comps, setComps] = useState([]);
  const [compSaving, setCompSaving] = useState(false);
  const [compNote, setCompNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [photoBusy, setPhotoBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [travelModal, setTravelModal] = useState(null); // { id|null, form }
  const [newTask, setNewTask] = useState({ title: '', notes: '' });
  const [editTask, setEditTask] = useState(null); // { id, title, notes }
  const [partModal, setPartModal] = useState(null); // { id|null, form }
  const [qLink, setQLink] = useState(null);
  const [qBusy, setQBusy] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api(`/jobs?business_id=${activeId}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load jobs.'); })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
    api('/jobs/ingest/status').then((s) => { setIngestEnabled(!!s.enabled); setMailEnabled(!!s.mail); }).catch(() => { setIngestEnabled(false); setMailEnabled(false); });
    api('/jobs/staff-options').then((d) => setStaffOptions(Array.isArray(d) ? d : [])).catch(() => setStaffOptions([]));
  }, [activeId]);

  function openNew() { setOpenRowInfo(null); setCurrent(null); setForm(emptyForm()); setFormError(null); setOpen(true); }
  function syncForm(j) {
    setForm({
      customer_id: j.customer_id, type: j.type, title: j.title || '', description: j.description || '',
      status: j.status, location: j.location || '', scheduled_date: j.scheduled_date?.slice(0, 10) || '',
      is_contract: !!j.is_contract,
      move_from: { ...emptyAddr(), ...(j.move_from || {}) }, move_to: { ...emptyAddr(), ...(j.move_to || {}) },
    });
  }
  async function openRow(row) {
    setFormError(null); setCurrent(null); setForm(emptyForm()); setOpenRowInfo(row); setOpen(true);
    try { const j = await api(`/jobs/${row.id}`); setCurrent(j); syncForm(j); }
    catch (e) { setFormError(e?.detail || 'Could not load this job — please try again.'); }
  }
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const refreshCurrent = useCallback(async () => {
    if (!current) return;
    try { setCurrent(await api(`/jobs/${current.id}`)); } catch { /* ignore */ }
  }, [current]);

  // Keep the editable component list in sync with the loaded job.
  useEffect(() => { setComps((current?.components || []).map((c) => ({ ...c, cost: c.cost ?? '' }))); setCompNote(null); }, [current]);
  const addComp = () => setComps((cs) => [...cs, { category: COMPONENT_CATEGORIES[0], manufacturer: '', model_number: '', serial_number: '', cost: '' }]);
  const setComp = (i, k, v) => setComps((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const removeComp = (i) => setComps((cs) => cs.filter((_, idx) => idx !== i));
  const compTotal = comps.reduce((s, c) => s + (Number(c.cost) || 0), 0);
  async function saveComps() {
    if (!current) return;
    setCompSaving(true); setCompNote(null);
    try {
      const updated = await api(`/jobs/${current.id}/components`, {
        method: 'PUT',
        body: { components: comps.filter((c) => (c.category || '').trim()) },
      });
      setCurrent(updated);
      setCompNote({ ok: true, text: 'Components saved.' });
    } catch (e) { setCompNote({ ok: false, text: e?.detail || 'Could not save components.' }); }
    finally { setCompSaving(false); }
  }

  // When a customer is chosen on a NEW job, default contract status from that customer (if known).
  function chooseCustomer(cid) {
    const c = customers.find((x) => String(x.id) === String(cid));
    setForm((f) => ({ ...f, customer_id: cid, is_contract: c?.is_contract ?? f.is_contract }));
  }

  async function save() {
    if (!form.customer_id) { setFormError('Choose a customer.'); return; }
    setSaving(true); setFormError(null);
    const body = { business_id: activeId, ...form, customer_id: Number(form.customer_id), scheduled_date: form.scheduled_date || null };
    if (form.type !== 'sim_move') { delete body.move_from; delete body.move_to; }
    try {
      if (current) await api(`/jobs/${current.id}`, { method: 'PUT', body });
      else await api('/jobs', { method: 'POST', body });
      setOpen(false); load();
    } catch (e) { setFormError(e?.detail || 'Could not save the job.'); }
    finally { setSaving(false); }
  }

  async function checkMailbox() {
    setChecking(true); setNotice(null);
    // Each request processes a small batch (OCR is slow); processed messages are marked read, so we
    // keep calling until a run finds nothing left — one click drains the whole mailbox.
    let created = 0, itineraries = 0, processed = 0, unmatched = 0, errors = 0, cut = false, sampleError = null;
    try {
      for (let i = 0; i < 25; i++) {
        const s = await api('/jobs/ingest', { method: 'POST' });
        created += s.expenses_created || 0;
        itineraries += s.itineraries_created || 0;
        processed += s.processed || 0;
        unmatched += s.unmatched?.length || 0;
        errors += s.errors?.length || 0;
        if (!sampleError && s.errors?.length) sampleError = s.errors[0]?.error || null;
        if (!s.processed) break;                       // mailbox empty
        // Nothing filed and every message errored → the same messages will keep coming back
        // (they aren't marked read). Stop rather than spin, and show why.
        if (!s.expenses_created && !s.itineraries_created && s.errors?.length >= s.processed) break;
        if (i === 24) cut = true;                       // hit the safety cap with messages still pending
      }
      const parts = [`${created} receipt${created === 1 ? '' : 's'} filed from ${processed} message${processed === 1 ? '' : 's'}`];
      if (itineraries) parts.push(`${itineraries} travel itinerar${itineraries === 1 ? 'y' : 'ies'} added to jobs`);
      if (unmatched) parts.push(`${unmatched} with no job match (filed for review)`);
      if (errors) parts.push(`${errors} error${errors === 1 ? '' : 's'}`);
      if (cut) parts.push('more may remain — click again');
      let msg = parts.join(' · ') + '.';
      if (sampleError && created === 0 && itineraries === 0) msg += ` First error: ${sampleError}`;
      setNotice(msg);
      load();
    } catch (e) {
      const tail = processed ? ` (filed ${created} from ${processed} so far — click again to continue)` : '';
      setNotice((e?.detail || 'Mailbox check failed.') + tail);
      load();
    } finally { setChecking(false); }
  }

  async function diagnoseMailbox() {
    setChecking(true); setNotice(null);
    try {
      const r = await api('/jobs/ingest/diagnose');
      setDiag(r);
    } catch (e) { setNotice(e?.detail || 'Mailbox diagnose failed.'); }
    finally { setChecking(false); }
  }

  // ---- staff ----
  const assignedIds = new Set((current?.staff || []).map((s) => s.user_id));
  async function toggleStaff(userId) {
    if (!current || !editable) return;
    const next = new Set(assignedIds);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    const staff = [...next].map((id) => ({ user_id: id }));
    try { setCurrent(await api(`/jobs/${current.id}/staff`, { method: 'PUT', body: { staff } })); } catch { /* ignore */ }
  }

  // ---- photos ----
  async function onPickPhotos(kind, files) {
    if (!current || !files?.length) return;
    setPhotoBusy(true);
    try {
      for (const file of Array.from(files)) {
        const image = await resizeImage(file);
        await api(`/jobs/${current.id}/photos`, { method: 'POST', body: { kind, image } });
      }
      await refreshCurrent();
    } catch (e) { setNotice(e?.message || 'Photo upload failed.'); }
    finally { setPhotoBusy(false); }
  }
  async function deletePhoto(photoId) {
    try { await api(`/jobs/${current.id}/photos/${photoId}`, { method: 'DELETE' }); await refreshCurrent(); } catch { /* ignore */ }
  }

  // ---- travel ----
  function openTravel(rec) {
    if (rec) {
      setTravelModal({ id: rec.id, form: {
        staff_id: rec.staff_id || '', depart_at: toLocalInput(rec.depart_at), return_at: toLocalInput(rec.return_at),
        airline: rec.airline || '', flight_confirmation: rec.flight_confirmation || '',
        flight_numbers: rec.flight_numbers || '', flight_route: rec.flight_route || '',
        hotel_name: rec.hotel_name || '', hotel_confirmation: rec.hotel_confirmation || '',
        hotel_check_in: rec.hotel_check_in?.slice(0, 10) || '', hotel_check_out: rec.hotel_check_out?.slice(0, 10) || '',
        car_company: rec.car_company || '', car_confirmation: rec.car_confirmation || '',
        car_pickup_at: toLocalInput(rec.car_pickup_at), car_return_at: toLocalInput(rec.car_return_at), notes: rec.notes || '',
      } });
    } else setTravelModal({ id: null, form: emptyTravel() });
  }
  const setTravelField = (k, v) => setTravelModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));
  const setPartField = (k, v) => setPartModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));
  async function saveTravel() {
    const { id, form: tf } = travelModal;
    try {
      const j = id
        ? await api(`/jobs/${current.id}/travel/${id}`, { method: 'PUT', body: tf })
        : await api(`/jobs/${current.id}/travel`, { method: 'POST', body: tf });
      setCurrent(j); setTravelModal(null);
    } catch (e) { setNotice(e?.detail || 'Could not save travel.'); }
  }
  async function deleteTravel(tid) {
    try { setCurrent(await api(`/jobs/${current.id}/travel/${tid}`, { method: 'DELETE' })); } catch { /* ignore */ }
  }

  async function addTask() {
    const title = (newTask.title || '').trim();
    if (!title) return;
    try {
      setCurrent(await api(`/jobs/${current.id}/tasks`, { method: 'POST', body: { title, notes: newTask.notes || null } }));
      setNewTask({ title: '', notes: '' });
    } catch (e) { setNotice(e?.detail || 'Could not add task.'); }
  }
  async function toggleTask(t) {
    try { setCurrent(await api(`/jobs/${current.id}/tasks/${t.id}`, { method: 'PUT', body: { is_complete: !t.is_complete } })); }
    catch (e) { setNotice(e?.detail || 'Could not update task.'); }
  }
  async function saveTaskEdit(t, fields) {
    try { setCurrent(await api(`/jobs/${current.id}/tasks/${t.id}`, { method: 'PUT', body: fields })); setEditTask(null); }
    catch (e) { setNotice(e?.detail || 'Could not save task.'); }
  }
  async function deleteTask(tid) {
    if (!window.confirm('Delete this task?')) return;
    try { setCurrent(await api(`/jobs/${current.id}/tasks/${tid}`, { method: 'DELETE' })); } catch { /* ignore */ }
  }

  function openPart(p) {
    setPartModal(p
      ? { id: p.id, form: { part_name: p.part_name, sku: p.sku || '', unit_price: p.unit_price ?? '', quantity: p.quantity ?? 1, ordered_from: p.ordered_from || '', notes: p.notes || '' } }
      : { id: null, form: { part_name: '', sku: '', unit_price: '', quantity: 1, ordered_from: '', notes: '' } });
  }
  async function savePart() {
    const { id, form: pf } = partModal;
    if (!pf.part_name.trim()) { setNotice('Part name is required.'); return; }
    try {
      const j = id
        ? await api(`/jobs/${current.id}/parts/${id}`, { method: 'PUT', body: pf })
        : await api(`/jobs/${current.id}/parts`, { method: 'POST', body: pf });
      setCurrent(j); setPartModal(null);
    } catch (e) { setNotice(e?.detail || 'Could not save part.'); }
  }
  async function deletePart(pid) {
    if (!window.confirm('Delete this part?')) return;
    try { setCurrent(await api(`/jobs/${current.id}/parts/${pid}`, { method: 'DELETE' })); } catch (e) { setNotice(e?.detail || 'Could not delete part.'); }
  }
  async function billPart(pid) {
    try { setCurrent(await api(`/jobs/${current.id}/parts/${pid}/bill`, { method: 'POST' })); }
    catch (e) { setNotice(e?.detail || 'Could not bill the part.'); }
  }
  async function unbillPart(pid) {
    try { setCurrent(await api(`/jobs/${current.id}/parts/${pid}/unbill`, { method: 'POST' })); }
    catch (e) { setNotice(e?.detail || 'Could not unbill the part.'); }
  }

  // ---- move addresses + questionnaire ----
  const setMoveAddr = (which, k, v) => setForm((f) => ({ ...f, [which]: { ...(f[which] || {}), [k]: v } }));
  async function generateLink() {
    if (!current) return;
    const wantEmail = sendEmail && mailEnabled && !!current.customer_email;
    setQBusy(true); setQLink(null);
    try {
      const r = await api(`/jobs/${current.id}/questionnaire`, { method: 'POST', body: { send_email: wantEmail } });
      setQLink(r.link);
      if (r.emailed) {
        setNotice(`Questionnaire link emailed to ${r.email_to}.`);
      } else {
        try { await navigator.clipboard.writeText(r.link); } catch { /* clipboard may be blocked */ }
        setNotice(wantEmail ? 'Could not email the link — copied to your clipboard instead.' : 'Customer questionnaire link copied to clipboard.');
      }
      await refreshCurrent();
    } catch (e) { setNotice(e?.detail || 'Could not create the link.'); }
    finally { setQBusy(false); }
  }

  const columns = [
    { key: 'job_number', header: 'Job #', render: (r) => <span className="mono">{r.job_number}</span> },
    { key: 'customer_name', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'type', header: 'Type', render: (r) => typeLabel(r.type) },
    { key: 'is_contract', header: 'Billing', render: (r) => <span className={`pill pill--${r.is_contract ? 'contract' : 'noncontract'}`}>{r.is_contract ? 'Contract' : 'Non-contract'}</span> },
    { key: 'staff_count', header: 'Crew', render: (r) => (Number(r.staff_count) || '—') },
    { key: 'expense_total', header: 'Receipts', render: (r) => (Number(r.expense_count) ? <span>{r.expense_count} · <span className="num">{money(r.expense_total)}</span></span> : '—') },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const loadingExisting = openRowInfo && !current;
  async function deleteJob() {
    if (!current) return;
    if (!window.confirm(`Delete ${current.job_number}? This permanently removes the job and its notes, photos, and travel. This can’t be undone.`)) return;
    setSaving(true); setFormError(null);
    try { await api(`/jobs/${current.id}`, { method: 'DELETE' }); setOpen(false); load(); }
    catch (e) { setFormError(e?.detail || 'Could not delete the job.'); }
    finally { setSaving(false); }
  }

  const footer = (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Close</button>
      {current && user?.role === 'admin' && <button className="btn btn--danger" onClick={deleteJob} disabled={saving}>Delete</button>}
      {editable && <button className="btn btn--primary" onClick={save} disabled={saving || loadingExisting}>{saving ? 'Saving…' : loadingExisting ? 'Loading…' : current ? 'Save changes' : 'Create job'}</button>}
    </>
  );

  const photosByKind = (k) => (current?.photos || []).filter((p) => p.kind === k);

  return (
    <>
      <PageHeader
        title="Jobs"
        actions={editable && (
          <div className="header-actions">
            {ingestEnabled && <button className="btn" onClick={checkMailbox} disabled={checking}>{checking ? 'Checking…' : 'Check mailbox now'}</button>}
            {ingestEnabled && <button className="btn" onClick={diagnoseMailbox} disabled={checking} title="Show what the receipts mailbox returns, without changing anything">Diagnose</button>}
            <button className="btn btn--primary" onClick={openNew}>New job</button>
          </div>
        )}
      />
      {notice && <div className="notice">{notice}<button className="notice__x" onClick={() => setNotice(null)}>×</button></div>}

      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} error={error}
          getRowKey={(r) => r.id} onRowClick={openRow}
          empty="No jobs yet. Create one for a sim repair or simulator move." />
      </section>

      <Drawer open={open} title={current ? current.job_number : (openRowInfo ? openRowInfo.job_number : 'New job')} width={760} onClose={() => !saving && setOpen(false)} footer={footer}>
        {formError && <div className="form-error">{formError}</div>}

        {current && (
          <div className="inv-status-row">
            <StatusBadge status={current.status} />
            <span className={`pill pill--${current.is_contract ? 'contract' : 'noncontract'}`}>{current.is_contract ? 'Contract' : 'Non-contract'}</span>
            <span className="hint">Email it with <strong>REC{current.job_number}</strong> (receipt) or <strong>ITN{current.job_number}</strong> (itinerary) in the subject.</span>
          </div>
        )}

        {/* ---- Details ---- */}
        <div className="field"><label>Customer *</label>
          <select value={form.customer_id} disabled={!editable} onChange={(e) => chooseCustomer(e.target.value)}>
            <option value="">Select a customer…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
          </select>
        </div>
        <div className="form-grid">
          <div className="field"><label>Type</label>
            <select value={form.type} disabled={!editable} onChange={(e) => setField('type', e.target.value)}>
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="field"><label>Status</label>
            <select value={form.status} disabled={!editable} onChange={(e) => setField('status', e.target.value)}>
              <option value="open">Open</option><option value="in_progress">In progress</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="field"><label>Scheduled date</label>
            <input type="date" value={form.scheduled_date} disabled={!editable} onChange={(e) => setField('scheduled_date', e.target.value)} /></div>
        </div>
        <label className="checkrow">
          <input type="checkbox" checked={form.is_contract} disabled={!editable} onChange={(e) => setField('is_contract', e.target.checked)} />
          <span>Contract client (covered work) — leave unchecked for non-contract / billed-per-job.</span>
        </label>
        <div className="field"><label>Title</label>
          <input value={form.title} disabled={!editable} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. King Air B200 G1000 — annual service" /></div>
        <div className="field"><label>Location</label>
          <input value={form.location} disabled={!editable} onChange={(e) => setField('location', e.target.value)} placeholder="Site / address" /></div>
        <div className="field"><label>Description</label>
          <textarea rows={2} value={form.description} disabled={!editable} onChange={(e) => setField('description', e.target.value)} /></div>

        {form.type === 'sim_move' && (
          <>
            <h3 className="form-section">Move addresses</h3>
            <div className="form-grid">
              <div className="field"><label>From — street</label><input value={form.move_from.line1 || ''} disabled={!editable} onChange={(e) => setMoveAddr('move_from', 'line1', e.target.value)} /></div>
              <div className="field"><label>From — floor</label><input value={form.move_from.floor || ''} disabled={!editable} onChange={(e) => setMoveAddr('move_from', 'floor', e.target.value)} /></div>
              <div className="field"><label>To — street</label><input value={form.move_to.line1 || ''} disabled={!editable} onChange={(e) => setMoveAddr('move_to', 'line1', e.target.value)} /></div>
              <div className="field"><label>To — floor</label><input value={form.move_to.floor || ''} disabled={!editable} onChange={(e) => setMoveAddr('move_to', 'floor', e.target.value)} /></div>
            </div>
            <p className="hint">Full addresses, room size, location photos and path videos are collected from the customer via the pre-move questionnaire below.</p>
          </>
        )}

        {!current && <p className="hint">Create the job to add crew, travel, and photos.</p>}

        {current && (
          <>
            {/* ---- Staff ---- */}
            <h3 className="form-section">Crew</h3>
            {staffOptions.length === 0 ? <p className="hint">No staff accounts yet.</p> : (
              <div className="staff-grid">
                {staffOptions.map((u) => (
                  <label className={`staff-chip${assignedIds.has(u.id) ? ' staff-chip--on' : ''}`} key={u.id}>
                    <input type="checkbox" checked={assignedIds.has(u.id)} disabled={!editable} onChange={() => toggleStaff(u.id)} />
                    <span>{u.name || u.email}</span>
                  </label>
                ))}
              </div>
            )}

            {/* ---- Issues & tasks (site visit) ---- */}
            {current.type === 'site_visit' && (
              <>
                <h3 className="form-section">Issues &amp; tasks
                  <span className="hint" style={{ fontWeight: 400 }}> {(current.tasks || []).filter((t) => t.is_complete).length}/{(current.tasks || []).length} done</span>
                </h3>
                {(current.tasks || []).length === 0 && <p className="hint">No tasks yet.</p>}
                <div className="task-list">
                  {(current.tasks || []).map((t) => (
                    <div className={'task' + (t.is_complete ? ' task--done' : '')} key={t.id}>
                      <label className="task__check">
                        <input type="checkbox" checked={t.is_complete} disabled={!editable} onChange={() => toggleTask(t)} />
                      </label>
                      <div className="task__body">
                        {editTask?.id === t.id ? (
                          <>
                            <input className="task__title-input" value={editTask.title} onChange={(e) => setEditTask({ ...editTask, title: e.target.value })} />
                            <textarea className="task__notes-input" rows={2} placeholder="Notes" value={editTask.notes} onChange={(e) => setEditTask({ ...editTask, notes: e.target.value })} />
                            <div className="task__acts">
                              <button className="btn btn--sm btn--primary" onClick={() => saveTaskEdit(t, { title: editTask.title, notes: editTask.notes })}>Save</button>
                              <button className="btn btn--sm" onClick={() => setEditTask(null)}>Cancel</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="task__title">{t.title}</div>
                            {t.notes && <div className="task__notes">{t.notes}</div>}
                            {t.is_complete && t.completed_at && <div className="task__done-at">Completed {date(t.completed_at)}</div>}
                          </>
                        )}
                      </div>
                      {editable && editTask?.id !== t.id && (
                        <div className="task__acts">
                          <button className="btn btn--sm" onClick={() => setEditTask({ id: t.id, title: t.title, notes: t.notes || '' })}>Edit</button>
                          <button className="btn btn--sm btn--ghost" onClick={() => deleteTask(t.id)}>Delete</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {editable && (
                  <div className="task-add">
                    <input placeholder="New task or issue…" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }} />
                    <input placeholder="Notes (optional)" value={newTask.notes} onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }} />
                    <button className="btn btn--sm btn--primary" onClick={addTask}>Add</button>
                  </div>
                )}
              </>
            )}

            {/* ---- Parts used ---- */}
            <h3 className="form-section">Parts used
              {editable && <button className="btn btn--sm form-section__act" onClick={() => openPart(null)}>+ Add</button>}
            </h3>
            {(current.parts || []).length === 0 ? <p className="hint">No parts recorded.</p> : (
              <div className="parts-list">
                {current.parts.map((p) => (
                  <div className="part-card" key={p.id}>
                    <div className="part-card__main">
                      <div className="part-card__row">
                        <strong>{p.part_name}</strong>
                        {p.sku && <span className="part-card__sku">SKU {p.sku}</span>}
                      </div>
                      <div className="part-card__meta">
                        {Number(p.quantity)} × {money(p.unit_price)} = <strong>{money(p.total)}</strong>{p.ordered_from ? ` · from ${p.ordered_from}` : ''}
                      </div>
                      {p.notes && <div className="part-card__notes">{p.notes}</div>}
                      {p.billed && <div className="part-card__billed">{p.on_invoice ? '✓ Billed — on an invoice' : '✓ Billed to customer (ready to invoice)'}</div>}
                    </div>
                    {editable && (
                      <div className="part-card__acts">
                        {!p.billed
                          ? <button className="btn btn--sm btn--primary" onClick={() => billPart(p.id)} title="Add as a billable expense on this job">Bill to customer</button>
                          : (!p.on_invoice && <button className="btn btn--sm" onClick={() => unbillPart(p.id)}>Unbill</button>)}
                        <button className="btn btn--sm" onClick={() => openPart(p)}>Edit</button>
                        <button className="btn btn--sm btn--ghost" onClick={() => deletePart(p.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ---- Travel ---- */}
            <h3 className="form-section">Travel &amp; lodging
              {editable && <button className="btn btn--sm form-section__act" onClick={() => openTravel(null)}>+ Add</button>}
            </h3>
            {(current.travel || []).length === 0 ? <p className="hint">No travel booked.</p> : (
              <div className="travel-list">
                {current.travel.map((t) => (
                  <div className="travel-card" key={t.id}>
                    <div className="travel-card__main">
                      <div className="travel-card__row"><strong>Travel</strong><span>{fmtDT(t.depart_at)} → {fmtDT(t.return_at)}</span></div>
                      {t.staff_name && <div className="travel-card__row"><span className="k">Who</span><span>{t.staff_name}</span></div>}
                      {(t.airline || t.flight_numbers || t.flight_confirmation) && <div className="travel-card__row"><span className="k">Flight</span><span>{[t.airline, t.flight_numbers, t.flight_route].filter(Boolean).join(' · ')}{t.flight_confirmation ? ` · #${t.flight_confirmation}` : ''}</span></div>}
                      {(t.hotel_name || t.hotel_confirmation) && <div className="travel-card__row"><span className="k">Hotel</span><span>{t.hotel_name || '—'}{t.hotel_confirmation ? ` · #${t.hotel_confirmation}` : ''}{t.hotel_check_in ? ` · ${date(t.hotel_check_in)}→${t.hotel_check_out ? date(t.hotel_check_out) : ''}` : ''}</span></div>}
                      {(t.car_company || t.car_confirmation) && <div className="travel-card__row"><span className="k">Car</span><span>{t.car_company || '—'}{t.car_confirmation ? ` · #${t.car_confirmation}` : ''}{t.car_pickup_at ? ` · ${fmtDT(t.car_pickup_at)}→${fmtDT(t.car_return_at)}` : ''}</span></div>}
                      {t.notes && <div className="travel-card__row"><span className="k">Notes</span><span>{t.notes}</span></div>}
                    </div>
                    {editable && (
                      <div className="travel-card__acts">
                        <button className="btn btn--sm" onClick={() => openTravel(t)}>Edit</button>
                        <button className="btn btn--sm btn--ghost" onClick={() => deleteTravel(t.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ---- Photos ---- */}
            <h3 className="form-section">Photos {photoBusy && <span className="hint">uploading…</span>}</h3>
            {['before', 'after'].map((kind) => (
              <div className="photo-group" key={kind}>
                <div className="photo-group__head">
                  <span className="photo-group__label">{kind === 'before' ? 'Before' : 'After'}</span>
                  {editable && (
                    <>
                      <button className="btn btn--sm" disabled={photoBusy} onClick={() => (kind === 'before' ? beforeRef : afterRef).current?.click()}>+ Add</button>
                      <input ref={kind === 'before' ? beforeRef : afterRef} type="file" accept="image/*" capture="environment" multiple
                        style={{ display: 'none' }} onChange={(e) => { onPickPhotos(kind, e.target.files); e.target.value = ''; }} />
                    </>
                  )}
                </div>
                {photosByKind(kind).length === 0 ? <p className="hint">No {kind} photos.</p> : (
                  <div className="photo-grid">
                    {photosByKind(kind).map((p) => (
                      <PhotoTile key={p.id} jobId={current.id} photo={p} canEdit={editable} onDelete={deletePhoto} onOpen={setLightbox} />
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* ---- Pre-move questionnaire ---- */}
            {form.type === 'sim_move' && (
              <>
                <h3 className="form-section">Pre-move questionnaire</h3>
                <div className="mq-panel">
                  <div className="inv-status-row">
                    <StatusBadge status={current.move_questionnaire?.status || 'draft'} />
                    {current.move_questionnaire?.submitted_at && <span className="hint">Submitted {date(current.move_questionnaire.submitted_at)}{current.move_questionnaire.submitted_by ? ` by ${current.move_questionnaire.submitted_by}` : ''}</span>}
                  </div>
                  {editable && (
                    <>
                      {mailEnabled && current.customer_email && (
                        <label className="checkrow" style={{ margin: '8px 0' }}>
                          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                          <span>Email the link to the customer ({current.customer_email})</span>
                        </label>
                      )}
                      {mailEnabled && !current.customer_email && <p className="hint">Add an email address to this customer to enable auto-send.</p>}
                      <button className="btn btn--sm" disabled={qBusy} onClick={generateLink}>
                        {(current.move_questionnaire ? 'Regenerate' : 'Create')}{sendEmail && mailEnabled && current.customer_email ? ' & email link' : ' customer link'}
                      </button>
                      {qLink && (
                        <div className="mq-link">
                          <input readOnly value={qLink} onFocus={(e) => e.target.select()} />
                          <button className="btn btn--sm" onClick={() => { navigator.clipboard?.writeText(qLink); setNotice('Link copied.'); }}>Copy</button>
                        </div>
                      )}
                    </>
                  )}
                  {current.move_questionnaire && (
                    <div className="mq-answers">
                      <span><span className="k">Simulator</span>{current.move_questionnaire.simulator_type || '—'}{current.move_questionnaire.serial_number ? ` · S/N ${current.move_questionnaire.serial_number}` : ''}{current.move_questionnaire.year_purchased ? ` · ${current.move_questionnaire.year_purchased}` : ''}</span>
                      <span><span className="k">New room size</span>{current.move_questionnaire.new_room_size || '—'}</span>
                      <span><span className="k">36&quot; doorways</span>{current.move_questionnaire.doorways_36_certified ? `Certified${current.move_questionnaire.certified_by ? ` by ${current.move_questionnaire.certified_by}` : ''}` : 'Not yet certified'}</span>
                      {current.move_questionnaire.move_info && <span><span className="k">Move info</span>{current.move_questionnaire.move_info}</span>}
                      {current.move_questionnaire.additional_work && <span><span className="k">Additional work</span>{current.move_questionnaire.additional_work}</span>}
                    </div>
                  )}
                  {['existing_photo', 'new_photo'].map((cat) => {
                    const items = (current.move_media || []).filter((m) => m.category === cat);
                    if (!items.length) return null;
                    return (
                      <div key={cat}>
                        <p className="hint" style={{ marginTop: 8 }}>{cat === 'existing_photo' ? 'Existing location' : 'New location'} photos</p>
                        <div className="mq-media-row">
                          {items.map((m) => <img key={m.id} src={`/api/jobs/${current.id}/move-media/${m.id}/file`} alt="" onClick={() => setLightbox(`/api/jobs/${current.id}/move-media/${m.id}/file`)} />)}
                        </div>
                      </div>
                    );
                  })}
                  {['removal_video', 'install_video'].map((cat) => {
                    const items = (current.move_media || []).filter((m) => m.category === cat);
                    if (!items.length) return null;
                    return (
                      <div key={cat}>
                        <p className="hint" style={{ marginTop: 8 }}>{cat === 'removal_video' ? 'Removal path' : 'Install path'} video</p>
                        {items.map((m) => (m.storage === 'url'
                          ? <a key={m.id} href={m.url} target="_blank" rel="noreferrer">Linked video ↗</a>
                          : <video key={m.id} controls preload="metadata" src={`/api/jobs/${current.id}/move-media/${m.id}/file`} style={{ width: '100%', maxHeight: 240, borderRadius: 8, background: '#000' }} />))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* ---- Field notes & completion (read-only) ---- */}
            {(current.notes?.length > 0 || current.completion) && (
              <>
                <h3 className="form-section">Field notes &amp; completion</h3>
                {current.completion && (
                  <div className="mq-panel">
                    <div className="inv-status-row">
                      <StatusBadge status="completed" />
                      {current.completion.completed_at && <span className="hint">Completed {date(current.completion.completed_at)}</span>}
                    </div>
                    {current.completion.summary && <p style={{ fontSize: 14, margin: '8px 0' }}>{current.completion.summary}</p>}
                    {current.completion.signature_data && (
                      <div style={{ marginTop: 6 }}>
                        <img src={current.completion.signature_data} alt="Customer signature" style={{ maxWidth: 320, width: '100%', background: '#fff', border: '1px solid var(--line)', borderRadius: 8 }} />
                        <div className="hint">Signed by {current.completion.signer_name || '—'}{current.completion.signed_at ? ` · ${date(current.completion.signed_at)}` : ''}</div>
                      </div>
                    )}
                  </div>
                )}
                {current.notes?.length > 0 && (
                  <div className="mq-answers">
                    {current.notes.map((n) => <span key={n.id}><span className="k">{date(n.created_at)}</span>{n.body}{n.author ? ` — ${n.author}` : ''}</span>)}
                  </div>
                )}
              </>
            )}

            {/* ---- Receipts ---- */}
            {current.expenses?.length > 0 && (
              <>
                <h3 className="form-section">Receipts &amp; expenses</h3>
                <div className="runs">
                  {current.expenses.map((e) => (
                    <div className="runs__row runs__row--link" key={e.id} role="button" tabIndex={0}
                      title="Open this expense"
                      onClick={() => navigate(`/expenses?open=${e.id}`)}
                      onKeyDown={(ev) => { if (ev.key === 'Enter') navigate(`/expenses?open=${e.id}`); }}>
                      <span>{e.expense_date ? date(e.expense_date) : '—'}</span>
                      <span>{e.vendor || '—'}{e.source === 'email' ? ' 📧' : ''}</span>
                      <StatusBadge status={e.status} /><span className="num">{money(e.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {form.type === 'sim_upgrade' && (current ? (
              <div className="components">
                <h3 className="form-section">Upgrade components</h3>
                <table className="rep-table comp-table">
                  <thead>
                    <tr><th>Component</th><th>Manufacturer</th><th>Model #</th><th>Serial #</th><th className="num">Cost</th><th /></tr>
                  </thead>
                  <tbody>
                    {comps.map((c, i) => (
                      <tr key={i}>
                        <td>{editable
                          ? <select value={c.category} onChange={(e) => setComp(i, 'category', e.target.value)}>
                              {COMPONENT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                          : c.category}</td>
                        <td>{editable ? <input value={c.manufacturer || ''} onChange={(e) => setComp(i, 'manufacturer', e.target.value)} /> : (c.manufacturer || '—')}</td>
                        <td>{editable ? <input value={c.model_number || ''} onChange={(e) => setComp(i, 'model_number', e.target.value)} /> : (c.model_number || '—')}</td>
                        <td>{editable ? <input value={c.serial_number || ''} onChange={(e) => setComp(i, 'serial_number', e.target.value)} /> : (c.serial_number || '—')}</td>
                        <td className="num">{editable
                          ? <input type="number" step="0.01" className="comp-cost" value={c.cost} onChange={(e) => setComp(i, 'cost', e.target.value)} />
                          : money(c.cost)}</td>
                        <td>{editable && <button type="button" className="li__rm" onClick={() => removeComp(i)} aria-label="Remove">×</button>}</td>
                      </tr>
                    ))}
                    {comps.length === 0 && <tr><td colSpan={6} className="hint" style={{ padding: '10px' }}>No components added yet.</td></tr>}
                  </tbody>
                  <tfoot><tr><td colSpan={4}>Total parts cost</td><td className="num">{money(compTotal)}</td><td /></tr></tfoot>
                </table>
                {editable && (
                  <div className="comp-actions">
                    <button type="button" className="btn btn--sm" onClick={addComp}>+ Add component</button>
                    <button type="button" className="btn btn--sm btn--primary" onClick={saveComps} disabled={compSaving}>{compSaving ? 'Saving…' : 'Save components'}</button>
                  </div>
                )}
                {compNote && <div className={compNote.ok ? 'form-note' : 'form-error'} style={{ marginTop: 10 }}>{compNote.text}</div>}
              </div>
            ) : (
              <p className="hint" style={{ marginTop: 16 }}>Save the job first, then add the upgrade components here.</p>
            ))}

            {current && <JobBilling job={current} />}
          </>
        )}
      </Drawer>

      {/* Lightbox */}
      <Modal open={!!lightbox} title="Photo" onClose={() => setLightbox(null)}>
        {lightbox && <img src={lightbox} alt="" style={{ maxWidth: '100%', borderRadius: 8 }} />}
      </Modal>

      {/* Mailbox diagnostic (read-only) */}
      <Modal open={!!diag} title="Mailbox diagnostic" onClose={() => setDiag(null)}
        footer={<>
          <button className="btn" onClick={() => { navigator.clipboard?.writeText(JSON.stringify(diag, null, 2)); setNotice('Diagnostic copied to clipboard.'); }}>Copy</button>
          <button className="btn btn--primary" onClick={() => setDiag(null)}>Close</button>
        </>}>
        {diag && (
          <div className="diag">
            <p className="hint">
              Reading <strong>{diag.mailbox || '(mailbox)'}</strong> — <strong>{diag.unreadSeen}</strong> unread message{diag.unreadSeen === 1 ? '' : 's'} seen.
              Nothing here is filed or marked read.
            </p>
            {(!diag.messages || diag.messages.length === 0) && (
              <p>No unread messages were returned by the mailbox query. If you expect a receipt here, confirm it's unread and in the Inbox of this mailbox (not a subfolder or already read).</p>
            )}
            {diag.messages?.map((m, i) => (
              <div key={i} className="diag__msg">
                <div className="diag__subj">{m.subject}</div>
                <div className="diag__meta">
                  from {m.from || '—'} · {m.received ? date(m.received) : '—'} · {m.hasAttachments ? `${m.attachments?.length || 0} attachment(s)` : 'no attachment'}
                </div>
                {m.attachments?.length > 0 && (
                  <ul className="diag__atts">
                    {m.attachments.map((a, j) => (
                      <li key={j}>{a.name || '(unnamed)'} — {a.type || '?'}{a.inline ? ', inline' : ''}{a.treatedAsLogo ? ' · treated as logo (skipped)' : ''}</li>
                    ))}
                  </ul>
                )}
                {m.body && (
                  <div className="diag__body">
                    <div>body total detected: <strong>{m.body.amountFound != null ? money(m.body.amountFound) : 'none'}</strong> · vendor guess: {m.body.vendorGuess || '—'} · {m.body.contentType}, {m.body.textLength} chars</div>
                    <pre className="diag__snip">{m.body.textSnippet || '(empty body)'}</pre>
                  </div>
                )}
                {m.bodyError && <div className="diag__err">body read error: {m.bodyError}</div>}
                {m.attachmentsError && <div className="diag__err">attachments error: {m.attachmentsError}</div>}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Travel add/edit */}
      <Modal open={!!partModal} title={partModal?.id ? 'Edit part' : 'Add part'} onClose={() => setPartModal(null)}
        footer={<><button className="btn" onClick={() => setPartModal(null)}>Cancel</button><button className="btn btn--primary" onClick={savePart}>Save</button></>}>
        {partModal && (
          <>
            <div className="field"><label>Part name</label>
              <input autoFocus value={partModal.form.part_name} onChange={(e) => setPartField('part_name', e.target.value)} /></div>
            <div className="form-grid">
              <div className="field"><label>SKU</label><input value={partModal.form.sku} onChange={(e) => setPartField('sku', e.target.value)} /></div>
              <div className="field"><label>Ordered from</label><input value={partModal.form.ordered_from} onChange={(e) => setPartField('ordered_from', e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>Unit price</label><input type="number" step="0.01" value={partModal.form.unit_price} onChange={(e) => setPartField('unit_price', e.target.value)} /></div>
              <div className="field"><label>Quantity</label><input type="number" step="1" value={partModal.form.quantity} onChange={(e) => setPartField('quantity', e.target.value)} /></div>
              <div className="field"><label>Total</label><input value={money((Number(partModal.form.unit_price) || 0) * (Number(partModal.form.quantity) || 0))} disabled readOnly /></div>
            </div>
            <div className="field"><label>Notes</label>
              <textarea rows={2} value={partModal.form.notes} onChange={(e) => setPartField('notes', e.target.value)} /></div>
            {partModal.id && <p className="hint">Editing the price or quantity also updates the billed expense, unless it's already on an invoice.</p>}
          </>
        )}
      </Modal>

      <Modal open={!!travelModal} title={travelModal?.id ? 'Edit travel' : 'Add travel'} onClose={() => setTravelModal(null)}
        footer={<><button className="btn" onClick={() => setTravelModal(null)}>Cancel</button><button className="btn btn--primary" onClick={saveTravel}>Save</button></>}>
        {travelModal && (
          <>
            <div className="field"><label>Who (optional)</label>
              <select value={travelModal.form.staff_id} onChange={(e) => setTravelField('staff_id', e.target.value)}>
                <option value="">— anyone —</option>
                {staffOptions.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div className="form-grid">
              <div className="field"><label>Depart</label><input type="datetime-local" value={travelModal.form.depart_at} onChange={(e) => setTravelField('depart_at', e.target.value)} /></div>
              <div className="field"><label>Return</label><input type="datetime-local" value={travelModal.form.return_at} onChange={(e) => setTravelField('return_at', e.target.value)} /></div>
            </div>
            <h4 className="form-subsection">Flight</h4>
            <div className="form-grid">
              <div className="field"><label>Airline</label><input value={travelModal.form.airline} onChange={(e) => setTravelField('airline', e.target.value)} /></div>
              <div className="field"><label>Confirmation #</label><input value={travelModal.form.flight_confirmation} onChange={(e) => setTravelField('flight_confirmation', e.target.value)} /></div>
              <div className="field"><label>Flight numbers</label><input value={travelModal.form.flight_numbers} onChange={(e) => setTravelField('flight_numbers', e.target.value)} /></div>
              <div className="field"><label>Route</label><input value={travelModal.form.flight_route} onChange={(e) => setTravelField('flight_route', e.target.value)} /></div>
            </div>
            <h4 className="form-subsection">Hotel</h4>
            <div className="form-grid">
              <div className="field"><label>Hotel</label><input value={travelModal.form.hotel_name} onChange={(e) => setTravelField('hotel_name', e.target.value)} /></div>
              <div className="field"><label>Confirmation #</label><input value={travelModal.form.hotel_confirmation} onChange={(e) => setTravelField('hotel_confirmation', e.target.value)} /></div>
              <div className="field"><label>Check-in</label><input type="date" value={travelModal.form.hotel_check_in} onChange={(e) => setTravelField('hotel_check_in', e.target.value)} /></div>
              <div className="field"><label>Check-out</label><input type="date" value={travelModal.form.hotel_check_out} onChange={(e) => setTravelField('hotel_check_out', e.target.value)} /></div>
            </div>
            <h4 className="form-subsection">Car rental</h4>
            <div className="form-grid">
              <div className="field"><label>Company</label><input value={travelModal.form.car_company} onChange={(e) => setTravelField('car_company', e.target.value)} /></div>
              <div className="field"><label>Confirmation #</label><input value={travelModal.form.car_confirmation} onChange={(e) => setTravelField('car_confirmation', e.target.value)} /></div>
              <div className="field"><label>Pick-up</label><input type="datetime-local" value={travelModal.form.car_pickup_at} onChange={(e) => setTravelField('car_pickup_at', e.target.value)} /></div>
              <div className="field"><label>Drop-off</label><input type="datetime-local" value={travelModal.form.car_return_at} onChange={(e) => setTravelField('car_return_at', e.target.value)} /></div>
            </div>
            <div className="field"><label>Notes</label><textarea rows={2} value={travelModal.form.notes} onChange={(e) => setTravelField('notes', e.target.value)} /></div>
          </>
        )}
      </Modal>
    </>
  );
}
